import React, { useMemo } from 'react';
import { AlertCircle, FileText } from 'lucide-react';
import { Badge } from './ui/badge';

export default function LayoutPreview({ badge, images, orientation, uploadMode }) {
  const layoutInfo = useMemo(() => {
    if (!badge) return null;

    // A4 dimensions in mm
    const a4Width = 210;
    const a4Height = 297;
    const margin = 10; // margin from edges

    let badgeWidth, badgeHeight;
    
    if (badge.type === 'round') {
      badgeWidth = badgeHeight = badge.designArea;
    } else if (badge.type === 'square') {
      badgeWidth = badgeHeight = badge.designArea;
    } else {
      // Rectangle - respect orientation
      if (orientation === 'landscape') {
        badgeWidth = badge.designWidth;
        badgeHeight = badge.designHeight;
      } else {
        badgeWidth = badge.designHeight;
        badgeHeight = badge.designWidth;
      }
    }

    // Calculate how many badges fit
    const availableWidth = a4Width - (2 * margin);
    const availableHeight = a4Height - (2 * margin);
    
    const cols = Math.floor(availableWidth / badgeWidth);
    const rows = Math.floor(availableHeight / badgeHeight);
    const totalBadges = cols * rows;

    // Calculate spacing
    const spacingX = cols > 1 ? (availableWidth - (cols * badgeWidth)) / (cols - 1) : 0;
    const spacingY = rows > 1 ? (availableHeight - (rows * badgeHeight)) / (rows - 1) : 0;

    return {
      cols,
      rows,
      totalBadges,
      badgeWidth,
      badgeHeight,
      spacingX,
      spacingY,
      margin,
      a4Width,
      a4Height
    };
  }, [badge, orientation]);

  if (!badge) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
        <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-2">No Badge Selected</h3>
        <p className="text-muted-foreground">Please select a badge size to see A4 layout</p>
      </div>
    );
  }

  const scale = 2.5; // Scale for display

  return (
    <div className="flex flex-col items-center space-y-6">
      {/* Info Cards */}
      <div className="grid grid-cols-3 gap-4 w-full max-w-2xl">
        <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-4 text-center">
          <div className="text-3xl font-bold text-primary mb-1">{layoutInfo.totalBadges}</div>
          <div className="text-sm text-muted-foreground">Badges per sheet</div>
        </div>
        <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 rounded-2xl p-4 text-center">
          <div className="text-3xl font-bold text-secondary mb-1">{layoutInfo.cols}×{layoutInfo.rows}</div>
          <div className="text-sm text-muted-foreground">Grid layout</div>
        </div>
        <div className="bg-gradient-to-br from-accent/10 to-accent/5 rounded-2xl p-4 text-center">
          <div className="text-3xl font-bold text-accent mb-1">
            {uploadMode === 'single' ? '1' : images.length}
          </div>
          <div className="text-sm text-muted-foreground">Design{uploadMode === 'single' ? '' : 's'}</div>
        </div>
      </div>

      {/* A4 Preview */}
      <div className="space-y-3 w-full max-w-2xl">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            A4 Layout Preview
          </h3>
          <Badge className="rounded-full bg-gradient-to-r from-primary to-secondary text-primary-foreground">
            {badge.label}
          </Badge>
        </div>

        <div className="bg-muted/30 rounded-2xl p-8 flex items-center justify-center overflow-auto">
          <div
            className="bg-white shadow-2xl relative"
            style={{
              width: `${layoutInfo.a4Width * scale}px`,
              height: `${layoutInfo.a4Height * scale}px`,
              minWidth: `${layoutInfo.a4Width * scale}px`,
              minHeight: `${layoutInfo.a4Height * scale}px`
            }}
          >
            {/* Margin guide */}
            <div
              className="absolute border-2 border-dashed border-muted-foreground/20"
              style={{
                left: `${layoutInfo.margin * scale}px`,
                top: `${layoutInfo.margin * scale}px`,
                right: `${layoutInfo.margin * scale}px`,
                bottom: `${layoutInfo.margin * scale}px`
              }}
            />

            {/* Badges */}
            {Array.from({ length: layoutInfo.rows }).map((_, row) =>
              Array.from({ length: layoutInfo.cols }).map((_, col) => {
                const index = row * layoutInfo.cols + col;
                const left = layoutInfo.margin + col * (layoutInfo.badgeWidth + layoutInfo.spacingX);
                const top = layoutInfo.margin + row * (layoutInfo.badgeHeight + layoutInfo.spacingY);
                
                // Determine which image to show
                let imageUrl = null;
                if (images.length > 0) {
                  if (uploadMode === 'single') {
                    imageUrl = images[0]?.url;
                  } else {
                    imageUrl = images[index % images.length]?.url;
                  }
                }

                return (
                  <div
                    key={`${row}-${col}`}
                    className="absolute border border-border/50 overflow-hidden"
                    style={{
                      left: `${left * scale}px`,
                      top: `${top * scale}px`,
                      width: `${layoutInfo.badgeWidth * scale}px`,
                      height: `${layoutInfo.badgeHeight * scale}px`,
                      borderRadius: badge.type === 'round' ? '50%' : '8px'
                    }}
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={`Badge ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted/30 flex items-center justify-center">
                        <span className="text-xs text-muted-foreground font-medium">
                          {index + 1}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="text-center text-sm text-muted-foreground">
          <p>This preview shows how badges will be arranged on A4 paper (210×297mm)</p>
        </div>
      </div>
    </div>
  );
}
